/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { Button, Input, message, Typography } from 'antd';
import Project from '~src/assets/nav-icons/project.png';
import { isEmailValid } from '~src/utils/login';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import {
  createUserPasswordForgottenToken,
  resendUserManagementVerificationEmail,
  resetUserPasswordWithPrivateKey,
  UserManagementLanguage,
  validateUserManagementToken,
} from '~src/services/api/userManagment';

const { Text, Link } = Typography;

interface ForgotPasswordFormProps {
  onBackToLogin?: () => void;
  switchTab?: (string) => void;
  isOnLoginPage?: boolean;
}

type ForgotPasswordStep = 1 | 2 | 3;

const SIMPLE_KEY_MAX_LENGTH = 10;
const RESEND_COOLDOWN_SECONDS = 60;

const getUserManagementLanguage = (locale: string): UserManagementLanguage => {
  if (locale.startsWith('da')) return 'da';
  if (locale.startsWith('de')) return 'de';
  if (locale.startsWith('sv')) return 'sv';
  return 'en';
};

// Keep only A–Z and 0–9, uppercase, max 10 chars
const sanitizeSimpleKey = (val: string): string =>
  val
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, SIMPLE_KEY_MAX_LENGTH);

// Format XXXX-XX-XXXX for display
const formatSimpleKeyForDisplay = (raw: string): string => {
  const cleaned = sanitizeSimpleKey(raw);
  const p1 = cleaned.slice(0, 4);
  const p2 = cleaned.slice(4, 6);
  const p3 = cleaned.slice(6, 10);
  const parts = [p1, p2, p3].filter(Boolean);
  return parts.join('-');
};

// Strong password check
const isPasswordStrong = (password: string): boolean => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password)
  );
};

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToLogin,
  switchTab,
  isOnLoginPage = true,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();

  const [step, setStep] = useState<ForgotPasswordStep>(1);

  // Feature 1: Cooldown state
  const [cooldown, setCooldown] = useState(0);

  const [formSubmittedStep1, setFormSubmittedStep1] = useState(false);
  const [formSubmittedStep2, setFormSubmittedStep2] = useState(false);
  const [formSubmittedStep3, setFormSubmittedStep3] = useState(false);

  const [email, setEmail] = useState('');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState<string | null>(null);

  const [verificationCodeRaw, setVerificationCodeRaw] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Cooldown Timer Effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldown]);

  const userManagementLanguage = useMemo(
    () => getUserManagementLanguage(intl.locale || 'en'),
    [intl.locale]
  );

  const emailInvalid = formSubmittedStep1 && !isEmailValid(email);

  // What send to backend (XXXXXXXXXX)
  const normalizedVerificationCode = sanitizeSimpleKey(verificationCodeRaw);

  const verificationCodeInvalid =
    formSubmittedStep2 && normalizedVerificationCode.length === 0;

  // Feature 2: Use new strength check
  const newPasswordInvalid =
    formSubmittedStep3 && !isPasswordStrong(newPassword);

  const passwordsMismatch =
    formSubmittedStep3 &&
    newPassword.length > 0 &&
    confirmNewPassword.length > 0 &&
    newPassword !== confirmNewPassword;

  const isStep1 = step === 1;
  const isStep2 = step === 2;
  const isStep3 = step === 3;

  /** Step 1: create forgotten token (EMAIL) */
  const [doCreateForgottenToken, createForgottenTokenMutation] = useMutation(
    createUserPasswordForgottenToken,
    {
      onSuccess: (data) => {
        const rawPublicKey = (data.public_key || '').toString();
        setPublicKey(rawPublicKey);

        // Move strictly to STEP 2
        setStep(2);

        // Start cooldown immediately upon entering step 2 (email just sent)
        setCooldown(RESEND_COOLDOWN_SECONDS);

        setFormSubmittedStep2(false);
        setFormSubmittedStep3(false);
        setVerificationCodeRaw('');
        setPrivateKey(null);
        setNewPassword('');
        setConfirmNewPassword('');

        message.success(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-email-sent',
          })
        );
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-email-error',
          })
        );
      },
    }
  );

  /** Step 2: validate token (CODE ONLY) */
  const [doValidateToken, validateTokenMutation] = useMutation(
    validateUserManagementToken,
    {
      onSuccess: (data) => {
        setPrivateKey(data.private_key);
        setStep(3);

        message.success(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-code-success',
          })
        );
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-code-error',
          })
        );
      },
    }
  );

  /** Step 3: reset password with private key */
  const [doResetPassword, resetPasswordMutation] = useMutation(
    resetUserPasswordWithPrivateKey,
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-success',
          })
        );
        if (isOnLoginPage) {
          onBackToLogin();
        } else {
          switchTab?.('user-login');
        }
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'tender-claim-forgot-password-reset-error',
          })
        );
      },
    }
  );

  /** Resend verification email */
  const [
    doResendVerificationEmail,
    resendVerificationEmailMutation,
  ] = useMutation(resendUserManagementVerificationEmail, {
    onSuccess: () => {
      // Start cooldown on successful resend
      setCooldown(RESEND_COOLDOWN_SECONDS);

      message.success(
        intl.formatMessage({
          id: 'tender-claim-forgot-password-resend-success',
        })
      );
    },
    onError: () => {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-forgot-password-resend-error',
        })
      );
    },
  });

  // STEP 1: EMAIL
  const handleSubmitStep1 = () => {
    setFormSubmittedStep1(true);

    const trimmedEmail = email.trim();
    const emailOK = isEmailValid(trimmedEmail);
    if (!emailOK) return;

    setEmail(trimmedEmail);

    doCreateForgottenToken({
      email: trimmedEmail,
      language: userManagementLanguage,
    });
  };

  // STEP 2: VERIFICATION CODE ONLY
  const handleSubmitStep2 = () => {
    setFormSubmittedStep2(true);

    if (!publicKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-forgot-password-missing-public-key',
        })
      );
      setStep(1);
      return;
    }

    if (!normalizedVerificationCode) return;

    doValidateToken({
      publicKey,
      simpleKey: normalizedVerificationCode,
    });
  };

  // STEP 3: NEW PASSWORD
  const handleSubmitStep3 = () => {
    setFormSubmittedStep3(true);

    if (!privateKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-forgot-password-missing-private-key',
        })
      );
      setStep(1);
      setPrivateKey(null);
      setPublicKey(null);
      return;
    }

    // Validate using new strength check
    if (!isPasswordStrong(newPassword) || passwordsMismatch) return;

    doResetPassword({
      privateKey,
      newPassword,
    });
  };

  const handleResendCode = () => {
    if (!publicKey) return;
    // Prevent manual trigger if cooldown is active
    if (cooldown > 0) return;

    doResendVerificationEmail({
      publicKey,
      language: userManagementLanguage,
    });
  };

  const isAnyLoading =
    createForgottenTokenMutation.isLoading ||
    validateTokenMutation.isLoading ||
    resetPasswordMutation.isLoading ||
    resendVerificationEmailMutation.isLoading;

  return (
    <div css={isOnLoginPage ? style.container : style.containerModal}>
      <h3 css={style.title}>
        <img src={Project} alt="" css={style.icon} />
        {intl.formatMessage({
          id: 'tender-claim-forgot-password-title',
        })}
      </h3>

      {/* EMAIL */}
      <p css={style.label}>
        {intl.formatMessage({ id: 'tender-claim-forgot-password-email-label' })}
        :
      </p>
      <Input
        css={emailInvalid && style.inputInvalid}
        placeholder={intl.formatMessage({
          id: 'tender-claim-forgot-password-email-placeholder',
        })}
        value={email}
        disabled={!isStep1}
        onChange={({ target: { value } }) => setEmail(value)}
      />
      {emailInvalid && isStep1 && (
        <p css={style.fieldError}>
          {intl.formatMessage({
            id: 'tender-claim-forgot-password-invalid-email',
          })}
        </p>
      )}

      {/* STEP 1 BUTTON: Send verification code */}
      {isStep1 && (
        <Button
          css={style.button}
          htmlType="button"
          loading={createForgottenTokenMutation.isLoading}
          onClick={handleSubmitStep1}>
          {intl.formatMessage({
            id: 'tender-claim-forgot-password-send-email',
          })}
        </Button>
      )}

      {/* STEP 2 & 3: VERIFICATION CODE + RESEND + VERIFY */}
      {!isStep1 && (
        <Fragment>
          <p css={style.label}>
            {intl.formatMessage({
              id: 'tender-claim-forgot-password-verification-code',
            })}
            :
          </p>

          <div css={style.codeRow}>
            <Input
              css={[
                style.inputMono,
                verificationCodeInvalid && isStep2 && style.inputInvalid,
              ]}
              placeholder={intl.formatMessage({
                id:
                  'tender-claim-forgot-password-verification-code-placeholder',
              })}
              value={formatSimpleKeyForDisplay(verificationCodeRaw)}
              maxLength={20}
              disabled={isStep3}
              onChange={({ target: { value } }) =>
                setVerificationCodeRaw(value)
              }
            />

            <Button
              css={style.resendButton}
              type="link"
              htmlType="button"
              loading={resendVerificationEmailMutation.isLoading}
              // Disable if step 3 OR cooldown is active
              disabled={isStep3 || cooldown > 0}
              onClick={() => {
                if (isStep2) handleResendCode();
              }}>
              {/* countdown in button text */}
              {cooldown > 0
                ? intl.formatMessage(
                    {
                      id: 'tender-claim-forgot-password-resend-wait',
                    },
                    { seconds: cooldown }
                  )
                : intl.formatMessage({
                    id: 'tender-claim-forgot-password-resend-code',
                  })}
            </Button>
          </div>

          {verificationCodeInvalid && isStep2 && (
            <p css={style.fieldError}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-verification-code-error',
              })}
            </p>
          )}

          {!isStep3 && (
            <Button
              css={style.button}
              htmlType="button"
              loading={validateTokenMutation.isLoading}
              disabled={isStep3}
              onClick={() => {
                if (isStep2) handleSubmitStep2();
              }}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-verify-code',
              })}
            </Button>
          )}
        </Fragment>
      )}

      {/* STEP 3: NEW PASSWORD FIELDS */}
      {isStep3 && (
        <Fragment>
          <p css={style.label}>
            {intl.formatMessage({
              id: 'tender-claim-forgot-password-new-password',
            })}
            :
          </p>
          <Input.Password
            css={
              (newPasswordInvalid || passwordsMismatch) && style.inputInvalid
            }
            placeholder={intl.formatMessage({
              id: 'tender-claim-forgot-password-new-password-placeholder',
            })}
            value={newPassword}
            onChange={({ target: { value } }) => setNewPassword(value)}
          />
          {newPasswordInvalid && (
            <div
              css={style.fieldError}
              style={{
                maxWidth: '310px',
                lineHeight: '1.5',
                whiteSpace: 'normal',
              }}>
              {/* Updated Error Message */}
              {newPasswordInvalid && (
                <span>
                  {intl.formatMessage({
                    id: 'tender-claim-forgot-password-password-requirements',
                  })}
                </span>
              )}
            </div>
          )}

          <p css={style.label}>
            {intl.formatMessage({
              id: 'tender-claim-forgot-password-confirm-new-password',
            })}
            :
          </p>
          <Input.Password
            css={
              (newPasswordInvalid || passwordsMismatch) && style.inputInvalid
            }
            placeholder={intl.formatMessage({
              id:
                'tender-claim-forgot-password-confirm-new-password-placeholder',
            })}
            value={confirmNewPassword}
            onChange={({ target: { value } }) => setConfirmNewPassword(value)}
          />

          {passwordsMismatch && (
            <div
              css={style.fieldError}
              style={{ display: 'flex', flexDirection: 'column' }}>
              {passwordsMismatch && (
                <p>
                  {intl.formatMessage({
                    id: 'tender-claim-forgot-password-mismatch',
                  })}
                </p>
              )}
            </div>
          )}

          <Button
            css={style.button}
            htmlType="button"
            loading={resetPasswordMutation.isLoading}
            onClick={handleSubmitStep3}>
            {intl.formatMessage({
              id: 'tender-claim-forgot-password-reset-button',
            })}
          </Button>
        </Fragment>
      )}

      {isOnLoginPage ? (
        <a
          css={[style.link, isAnyLoading && style.disabledLink]}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (isAnyLoading) return;
            if (isOnLoginPage) {
              onBackToLogin();
            } else {
              switchTab?.('user-login');
            }
          }}>
          {intl.formatMessage({
            id: 'tender-claim-forgot-password-back-to-login',
          })}
        </a>
      ) : (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-have-account',
              })}
            </Text>
            <Link onClick={() => switchTab?.('user-login')}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-login',
              })}
            </Link>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-no-account',
              })}
            </Text>
            <Link onClick={() => switchTab?.('create-new-user')}>
              {intl.formatMessage({
                id: 'tender-claim-forgot-password-create-account',
              })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    minWidth: '350px',
    height: '100vh',
    backgroundColor: theme.colors.white,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  containerModal: {
    minWidth: '350px',
    backgroundColor: theme.colors.white,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    marginTop: '20px',
  },
  link: {
    marginTop: '20px',
    color: 'black',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  disabledLink: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  button: {
    textTransform: 'uppercase',
    backgroundColor: theme.colors.orange,
    border: 'none',
    color: theme.colors.white,
    marginTop: '20px',
    ':hover': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
    ':focus': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
  },
  icon: {
    width: '20px',
    marginRight: '10px',
  },
  inputInvalid: {
    border: `2px solid ${theme.colors.red}`,
  },
  fieldError: {
    marginTop: '6px',
    color: theme.colors.red,
    fontSize: '13px',
  },
  inputMono: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    letterSpacing: 2,
    textAlign: 'center',
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  resendButton: {
    marginTop: 0,
    padding: 0,
  },
});

export default ForgotPasswordForm;
